/**
 * Application-wide constants.
 *
 * Centralized location for shared constants used across the application.
 * Prevents duplication and ensures consistency.
 *
 * @module constants
 */

import { DayOfWeek, GermanLevel } from '@/types/partner-finder';

/**
 * Set of valid day names for day-of-week validation
 */
export const VALID_DAYS: Set<DayOfWeek> = new Set([
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]);

// ============================================================================
// Time Constants (for React Query and other time-based operations)
// ============================================================================

/**
 * Five minutes in milliseconds
 * Used for React Query staleTime configuration
 */
export const STALE_TIME_FIVE_MINUTES = 1000 * 60 * 5;

/**
 * Two minutes in milliseconds
 * Used for React Query staleTime configuration
 */
export const STALE_TIME_TWO_MINUTES = 1000 * 60 * 2;

// ============================================================================
// Default Level Constants
// ============================================================================

/**
 * Default German proficiency level
 * Used when level cannot be determined from context
 */
export const DEFAULT_LEVEL: GermanLevel = 'A1';

/**
 * Default partner level for conversations
 * Used when partner level is not available from API
 */
export const DEFAULT_PARTNER_LEVEL: GermanLevel = 'B1';

// ============================================================================
// Exam Simulation Constants
// ============================================================================

/**
 * Review period duration in seconds
 * Time allowed for reviewing answers after audio playback completes
 */
export const REVIEW_TIME_SECONDS = 5 * 60; // 5 minutes

/**
 * Passing score percentage threshold
 * Minimum score percentage required to pass an exam module
 */
export const PASSING_SCORE_PERCENTAGE = 60;

/**
 * Default reading module time in seconds
 * Used when module timing is not provided in settings
 */
export const DEFAULT_READING_TIME_SECONDS = 45 * 60; // 45 minutes

/**
 * Default number of audio plays remaining
 * Initial play count for listening module audio playback
 */
export const DEFAULT_PLAYS_REMAINING = 2;

/**
 * Pause in seconds between auto-advancing listening sections.
 * Must stay in sync with the setTimeout delay in useListeningModuleState.
 */
export const LISTENING_INTER_SECTION_PAUSE_SECONDS = 15;

/**
 * Critical time threshold in seconds
 * Time remaining when timer should show critical/warning state
 */
export const CRITICAL_TIME_SECONDS = 5 * 60; // 5 minutes

/**
 * Low time threshold in seconds
 * Time remaining when timer should show low time warning
 */
export const LOW_TIME_SECONDS = 10 * 60; // 10 minutes

// ============================================================================
// Admin Constants
// ============================================================================

/**
 * CEFR (Common European Framework of Reference) proficiency levels
 */
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

/**
 * User roles in the system
 */
export const USER_ROLES = ['student', 'instructor', 'admin'] as const;

/**
 * User account statuses
 */
export const USER_STATUSES = [
  'active',
  'inactive',
  'verified',
  'unverified',
] as const;

/**
 * Language skill modules
 */
export const MODULES = [
  { value: 'reading', label: 'Reading' },
  { value: 'listening', label: 'Listening' },
  { value: 'writing', label: 'Writing' },
  { value: 'speaking', label: 'Speaking' },
] as const;

/**
 * Exam type providers
 */
export const EXAM_TYPES = [
  { value: 'goethe', label: 'Goethe-Zertifikat' },
  { value: 'ösd', label: 'ÖSD Zertifikat' },
  { value: 'telc', label: 'telc Deutsch' },
] as const;

/**
 * Section numbers (1-10)
 */
export const SECTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * Valid audio file MIME types
 */
export const VALID_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/webm',
  'audio/ogg',
  'audio/mp3',
] as const;

/**
 * Valid image file MIME types
 */
export const VALID_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;
